import { useState, useEffect } from 'react';
import { useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useNavigate } from 'react-router-dom';
import { 
  Settings, 
  Truck,
  CreditCard,
  Package,
  Loader2,
  Plus,
  Edit,
  Trash2,
  X,
  Save,
  ArrowLeft
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../store/useStore';
import { supabase } from '../lib/supabase';
import type { Database } from '../types/database.types';

type Carrier = Database['public']['Tables']['carriers']['Row'];
type PaymentMethod = Database['public']['Tables']['payment_methods']['Row'];
type PackageType = Database['public']['Tables']['package_types']['Row'];

const carrierSchema = z.object({
  name: z.string().min(1, 'Nome corriere richiesto'),
  code: z.string().optional(),
  contact_info: z.string().optional(),
  is_active: z.boolean().default(true)
});

const paymentMethodSchema = z.object({
  name: z.string().min(1, 'Nome modalità pagamento richiesto'),
  code: z.string().optional(),
  description: z.string().optional(),
  is_active: z.boolean().default(true)
});

const packageTypeSchema = z.object({
  name: z.string().min(1, 'Nome tipo collo richiesto'),
  code: z.string().optional(),
  description: z.string().optional(),
  is_active: z.boolean().default(true)
});

type CarrierForm = z.infer<typeof carrierSchema>;
type PaymentMethodForm = z.infer<typeof paymentMethodSchema>;
type PackageTypeForm = z.infer<typeof packageTypeSchema>;

export const DocumentConfigurationPage = () => {
  const navigate = useNavigate();
  const [carriers, setCarriers] = useState<Carrier[]>([]);
  const [paymentMethods, setPaymentMethods] = useState<PaymentMethod[]>([]);
  const [packageTypes, setPackageTypes] = useState<PackageType[]>([]);
  const [loadingCarriers, setLoadingCarriers] = useState(true);
  const [loadingPaymentMethods, setLoadingPaymentMethods] = useState(true);
  const [loadingPackageTypes, setLoadingPackageTypes] = useState(true);
  const [showCarrierDialog, setShowCarrierDialog] = useState(false);
  const [showPaymentMethodDialog, setShowPaymentMethodDialog] = useState(false);
  const [showPackageTypeDialog, setShowPackageTypeDialog] = useState(false);
  const [editingCarrier, setEditingCarrier] = useState<Carrier | null>(null);
  const [editingPaymentMethod, setEditingPaymentMethod] = useState<PaymentMethod | null>(null);
  const [editingPackageType, setEditingPackageType] = useState<PackageType | null>(null);
  const [carrierToDelete, setCarrierToDelete] = useState<Carrier | null>(null);
  const [paymentMethodToDelete, setPaymentMethodToDelete] = useState<PaymentMethod | null>(null);
  const [packageTypeToDelete, setPackageTypeToDelete] = useState<PackageType | null>(null);
  const { addNotification } = useNotifications();
  const { profile } = useAuth();

  const {
    register: registerCarrier,
    handleSubmit: handleSubmitCarrier,
    formState: { errors: carrierErrors },
    reset: resetCarrier,
    setValue: setCarrierValue,
    watch: watchCarrier
  } = useForm<CarrierForm>({
    resolver: zodResolver(carrierSchema),
    defaultValues: {
      is_active: true
    }
  });

  const {
    register: registerPaymentMethod,
    handleSubmit: handleSubmitPaymentMethod,
    formState: { errors: paymentMethodErrors },
    reset: resetPaymentMethod,
    setValue: setPaymentMethodValue,
    watch: watchPaymentMethod
  } = useForm<PaymentMethodForm>({
    resolver: zodResolver(paymentMethodSchema),
    defaultValues: {
      is_active: true
    }
  });

  const {
    register: registerPackageType,
    handleSubmit: handleSubmitPackageType,
    formState: { errors: packageTypeErrors },
    reset: resetPackageType,
    setValue: setPackageTypeValue,
    watch: watchPackageType
  } = useForm<PackageTypeForm>({
    resolver: zodResolver(packageTypeSchema),
    defaultValues: {
      is_active: true
    }
  });

  const carrierIsActive = watchCarrier('is_active');
  const paymentMethodIsActive = watchPaymentMethod('is_active');
  const packageTypeIsActive = watchPackageType('is_active');

  const loadCarriers = useCallback(async () => {
    try {
      setLoadingCarriers(true);
      const { data, error } = await supabase
        .from('carriers')
        .select('*')
        .order('name');

      if (error) throw error;
      setCarriers(data || []);
    } catch (error: any) {
      console.error('Errore caricamento corrieri:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Errore nel caricamento dei corrieri'
      });
    } finally {
      setLoadingCarriers(false);
    }
  }, [addNotification]);

  const loadPaymentMethods = useCallback(async () => {
    try {
      setLoadingPaymentMethods(true);
      const { data, error } = await supabase
        .from('payment_methods')
        .select('*')
        .order('name');

      if (error) throw error;
      setPaymentMethods(data || []);
    } catch (error: any) {
      console.error('Errore caricamento modalità pagamento:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Errore nel caricamento delle modalità di pagamento'
      });
    } finally {
      setLoadingPaymentMethods(false);
    }
  }, [addNotification]);

  const loadPackageTypes = useCallback(async () => {
    try {
      setLoadingPackageTypes(true);
      const { data, error } = await supabase
        .from('package_types')
        .select('*')
        .order('name');

      if (error) throw error;
      setPackageTypes(data || []);
    } catch (error: any) {
      console.error('Errore caricamento tipi di colli:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: 'Errore nel caricamento dei tipi di colli'
      });
    } finally {
      setLoadingPackageTypes(false);
    }
  }, [addNotification]);

  useEffect(() => {
    loadCarriers();
    loadPaymentMethods();
    loadPackageTypes();
  }, [loadCarriers, loadPaymentMethods, loadPackageTypes]);

  const handleOpenCarrierDialog = (carrier?: Carrier) => {
    if (carrier) {
      setEditingCarrier(carrier);
      setCarrierValue('name', carrier.name);
      setCarrierValue('code', carrier.code || '');
      setCarrierValue('contact_info', carrier.contact_info || '');
      setCarrierValue('is_active', carrier.is_active);
    } else {
      setEditingCarrier(null);
      resetCarrier({ name: '', code: '', contact_info: '', is_active: true });
    }
    setShowCarrierDialog(true);
  };

  const handleOpenPaymentMethodDialog = (paymentMethod?: PaymentMethod) => {
    if (paymentMethod) {
      setEditingPaymentMethod(paymentMethod);
      setPaymentMethodValue('name', paymentMethod.name);
      setPaymentMethodValue('code', paymentMethod.code || '');
      setPaymentMethodValue('description', paymentMethod.description || '');
      setPaymentMethodValue('is_active', paymentMethod.is_active);
    } else {
      setEditingPaymentMethod(null);
      resetPaymentMethod({ name: '', code: '', description: '', is_active: true });
    }
    setShowPaymentMethodDialog(true);
  };

  const handleOpenPackageTypeDialog = (packageType?: PackageType) => {
    if (packageType) {
      setEditingPackageType(packageType);
      setPackageTypeValue('name', packageType.name);
      setPackageTypeValue('code', packageType.code || '');
      setPackageTypeValue('description', packageType.description || '');
      setPackageTypeValue('is_active', packageType.is_active);
    } else {
      setEditingPackageType(null);
      resetPackageType({ name: '', code: '', description: '', is_active: true });
    }
    setShowPackageTypeDialog(true);
  };

  const handleSubmitCarrierForm = async (data: CarrierForm) => {
    try {
      if (editingCarrier) {
        const { error } = await supabase
          .from('carriers')
          .update({
            name: data.name,
            code: data.code || null,
            contact_info: data.contact_info || null,
            is_active: data.is_active
          })
          .eq('id', editingCarrier.id);

        if (error) throw error;

        addNotification({
          type: 'success',
          title: 'Corriere aggiornato',
          message: 'Il corriere è stato aggiornato con successo'
        });
      } else {
        const { error } = await supabase
          .from('carriers')
          .insert([{
            name: data.name,
            code: data.code || null,
            contact_info: data.contact_info || null,
            is_active: data.is_active
          }]);

        if (error) throw error;

        addNotification({
          type: 'success',
          title: 'Corriere creato',
          message: 'Il corriere è stato creato con successo'
        });
      }

      setShowCarrierDialog(false);
      resetCarrier();
      loadCarriers();
    } catch (error: any) {
      console.error('Errore salvataggio corriere:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: error.message || 'Errore nel salvataggio del corriere'
      });
    }
  };

  const handleSubmitPaymentMethodForm = async (data: PaymentMethodForm) => {
    try {
      if (editingPaymentMethod) {
        const { error } = await supabase
          .from('payment_methods')
          .update({
            name: data.name,
            code: data.code || null,
            description: data.description || null,
            is_active: data.is_active
          })
          .eq('id', editingPaymentMethod.id);

        if (error) throw error;

        addNotification({
          type: 'success',
          title: 'Modalità pagamento aggiornata',
          message: 'La modalità di pagamento è stata aggiornata con successo'
        });
      } else {
        const { error } = await supabase
          .from('payment_methods')
          .insert([{
            name: data.name,
            code: data.code || null,
            description: data.description || null,
            is_active: data.is_active
          }]);

        if (error) throw error;

        addNotification({
          type: 'success',
          title: 'Modalità pagamento creata',
          message: 'La modalità di pagamento è stata creata con successo'
        });
      }

      setShowPaymentMethodDialog(false);
      resetPaymentMethod();
      loadPaymentMethods();
    } catch (error: any) {
      console.error('Errore salvataggio modalità pagamento:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: error.message || 'Errore nel salvataggio della modalità di pagamento'
      });
    }
  };

  const handleDeleteCarrier = async () => {
    if (!carrierToDelete) return;

    try {
      const { error } = await supabase
        .from('carriers')
        .delete()
        .eq('id', carrierToDelete.id);

      if (error) throw error;

      addNotification({
        type: 'success',
        title: 'Corriere eliminato',
        message: 'Il corriere è stato eliminato con successo'
      });

      setCarrierToDelete(null);
      loadCarriers();
    } catch (error: any) {
      console.error('Errore eliminazione corriere:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: error.message || 'Errore nell\'eliminazione del corriere'
      });
    }
  };

  const handleDeletePaymentMethod = async () => {
    if (!paymentMethodToDelete) return;

    try {
      const { error } = await supabase
        .from('payment_methods')
        .delete()
        .eq('id', paymentMethodToDelete.id);

      if (error) throw error;

      addNotification({
        type: 'success',
        title: 'Modalità pagamento eliminata',
        message: 'La modalità di pagamento è stata eliminata con successo'
      });

      setPaymentMethodToDelete(null);
      loadPaymentMethods();
    } catch (error: any) {
      console.error('Errore eliminazione modalità pagamento:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: error.message || 'Errore nell\'eliminazione della modalità di pagamento'
      });
    }
  };

  const handleSubmitPackageTypeForm = async (data: PackageTypeForm) => {
    try {
      if (editingPackageType) {
        const { error } = await supabase
          .from('package_types')
          .update({
            name: data.name,
            code: data.code || null,
            description: data.description || null,
            is_active: data.is_active
          })
          .eq('id', editingPackageType.id);

        if (error) throw error;

        addNotification({
          type: 'success',
          title: 'Tipo collo aggiornato',
          message: 'Il tipo di collo è stato aggiornato con successo'
        });
      } else {
        const { error } = await supabase
          .from('package_types')
          .insert([{
            name: data.name,
            code: data.code || null,
            description: data.description || null,
            is_active: data.is_active
          }]);

        if (error) throw error;

        addNotification({
          type: 'success',
          title: 'Tipo collo creato',
          message: 'Il tipo di collo è stato creato con successo'
        });
      }

      setShowPackageTypeDialog(false);
      resetPackageType();
      loadPackageTypes();
    } catch (error: any) {
      console.error('Errore salvataggio tipo collo:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: error.message || 'Errore nel salvataggio del tipo di collo'
      });
    }
  };

  const handleDeletePackageType = async () => {
    if (!packageTypeToDelete) return;

    try {
      const { error } = await supabase
        .from('package_types')
        .delete()
        .eq('id', packageTypeToDelete.id);

      if (error) throw error;

      addNotification({
        type: 'success',
        title: 'Tipo collo eliminato',
        message: 'Il tipo di collo è stato eliminato con successo'
      });

      setPackageTypeToDelete(null);
      loadPackageTypes();
    } catch (error: any) {
      console.error('Errore eliminazione tipo collo:', error);
      addNotification({
        type: 'error',
        title: 'Errore',
        message: error.message || 'Errore nell\'eliminazione del tipo di collo'
      });
    }
  };

  const isAdmin = profile?.role === 'admin' || profile?.role === 'commerciale' || profile?.role === 'amministrazione';

  return (
    <div className="mx-auto w-full max-w-7xl p-0 md:p-6 space-y-4 md:space-y-6">
      <div className="flex items-center justify-between">
        <div className="flex items-start gap-2 md:gap-4 min-w-0">
          <Button
            variant="ghost"
            onClick={() => navigate('/invoices')}
            className="flex items-center gap-2 flex-shrink-0 px-2 md:px-6"
          >
            <ArrowLeft className="w-4 h-4" />
            <span className="hidden sm:inline">Indietro</span>
          </Button>
          <div>
            <h1 className="text-xl md:text-3xl font-bold text-gray-900 break-words">Configurazione Documenti</h1>
            <p className="text-sm text-gray-600 mt-1">Gestisci corrieri e modalità di pagamento per i documenti</p>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
        {/* Corrieri */}
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Truck className="w-5 h-5 text-primary-600" />
                <CardTitle>Corrieri</CardTitle>
              </div>
              {isAdmin && (
                <Button
                  onClick={() => handleOpenCarrierDialog()}
                  size="sm"
                  className="h-8"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Aggiungi
                </Button>
              )}
            </div>
            <CardDescription>Gestisci l'elenco dei corrieri disponibili</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingCarriers ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : carriers.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Truck className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>Nessun corriere configurato</p>
              </div>
            ) : (
              <div className="space-y-2">
                {carriers.map((carrier) => (
                  <div
                    key={carrier.id}
                    className="flex items-center justify-between gap-2 p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{carrier.name}</span>
                        {!carrier.is_active && (
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">Inattivo</span>
                        )}
                      </div>
                      {carrier.code && (
                        <p className="text-sm text-gray-500">Codice: {carrier.code}</p>
                      )}
                      {carrier.contact_info && (
                        <p className="text-sm text-gray-500">{carrier.contact_info}</p>
                      )}
                    </div>
                    {isAdmin && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenCarrierDialog(carrier)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setCarrierToDelete(carrier)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modalità di Pagamento */}
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <CreditCard className="w-5 h-5 text-green-600" />
                <CardTitle>Modalità di Pagamento</CardTitle>
              </div>
              {isAdmin && (
                <Button
                  onClick={() => handleOpenPaymentMethodDialog()}
                  size="sm"
                  className="h-8"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Aggiungi
                </Button>
              )}
            </div>
            <CardDescription>Gestisci le modalità di pagamento disponibili</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingPaymentMethods ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : paymentMethods.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <CreditCard className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>Nessuna modalità di pagamento configurata</p>
              </div>
            ) : (
              <div className="space-y-2">
                {paymentMethods.map((method) => (
                  <div
                    key={method.id}
                    className="flex items-center justify-between gap-2 p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{method.name}</span>
                        {!method.is_active && (
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">Inattivo</span>
                        )}
                      </div>
                      {method.code && (
                        <p className="text-sm text-gray-500">Codice: {method.code}</p>
                      )}
                      {method.description && (
                        <p className="text-sm text-gray-500">{method.description}</p>
                      )}
                    </div>
                    {isAdmin && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenPaymentMethodDialog(method)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPaymentMethodToDelete(method)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Tipi di Colli */}
        <Card>
          <CardHeader>
            <div className="flex flex-wrap items-center justify-between gap-2">
              <div className="flex items-center gap-2 min-w-0">
                <Package className="w-5 h-5 text-purple-600" />
                <CardTitle>Tipi di Colli</CardTitle>
              </div>
              {isAdmin && (
                <Button
                  onClick={() => handleOpenPackageTypeDialog()}
                  size="sm"
                  className="h-8"
                >
                  <Plus className="w-4 h-4 mr-1" />
                  Aggiungi
                </Button>
              )}
            </div>
            <CardDescription>Gestisci i tipi di colli disponibili</CardDescription>
          </CardHeader>
          <CardContent>
            {loadingPackageTypes ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : packageTypes.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                <Package className="w-12 h-12 mx-auto mb-2 text-gray-400" />
                <p>Nessun tipo di collo configurato</p>
              </div>
            ) : (
              <div className="space-y-2">
                {packageTypes.map((pkgType) => (
                  <div
                    key={pkgType.id}
                    className="flex items-center justify-between gap-2 p-3 border rounded-lg hover:bg-gray-50"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="font-medium">{pkgType.name}</span>
                        {!pkgType.is_active && (
                          <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded">Inattivo</span>
                        )}
                      </div>
                      {pkgType.code && (
                        <p className="text-sm text-gray-500">Codice: {pkgType.code}</p>
                      )}
                      {pkgType.description && (
                        <p className="text-sm text-gray-500">{pkgType.description}</p>
                      )}
                    </div>
                    {isAdmin && (
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleOpenPackageTypeDialog(pkgType)}
                          className="h-8 w-8 p-0"
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setPackageTypeToDelete(pkgType)}
                          className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialog Corriere */}
      <Dialog open={showCarrierDialog} onOpenChange={setShowCarrierDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingCarrier ? 'Modifica Corriere' : 'Nuovo Corriere'}</DialogTitle>
            <DialogDescription>
              {editingCarrier ? 'Modifica i dati del corriere' : 'Inserisci i dati del nuovo corriere'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitCarrier(handleSubmitCarrierForm)} className="space-y-4">
            <div>
              <Label htmlFor="carrier-name">Nome *</Label>
              <Input
                id="carrier-name"
                {...registerCarrier('name')}
                placeholder="Es: DHL Express"
              />
              {carrierErrors.name && (
                <p className="text-sm text-red-600 mt-1">{carrierErrors.name.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="carrier-code">Codice</Label>
              <Input
                id="carrier-code"
                {...registerCarrier('code')}
                placeholder="Es: DHL"
              />
            </div>
            <div>
              <Label htmlFor="carrier-contact">Contatti</Label>
              <Input
                id="carrier-contact"
                {...registerCarrier('contact_info')}
                placeholder="Es: Tel: 800-123456"
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="carrier-active"
                checked={carrierIsActive}
                onChange={(e) => setCarrierValue('is_active', e.target.checked)}
                className="w-4 h-4"
              />
              <Label htmlFor="carrier-active" className="cursor-pointer">Attivo</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowCarrierDialog(false)}>
                Annulla
              </Button>
              <Button type="submit">
                <Save className="w-4 h-4 mr-2" />
                {editingCarrier ? 'Salva modifiche' : 'Crea'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Modalità Pagamento */}
      <Dialog open={showPaymentMethodDialog} onOpenChange={setShowPaymentMethodDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPaymentMethod ? 'Modifica Modalità Pagamento' : 'Nuova Modalità Pagamento'}</DialogTitle>
            <DialogDescription>
              {editingPaymentMethod ? 'Modifica i dati della modalità di pagamento' : 'Inserisci i dati della nuova modalità di pagamento'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitPaymentMethod(handleSubmitPaymentMethodForm)} className="space-y-4">
            <div>
              <Label htmlFor="payment-name">Nome *</Label>
              <Input
                id="payment-name"
                {...registerPaymentMethod('name')}
                placeholder="Es: Bonifico Bancario"
              />
              {paymentMethodErrors.name && (
                <p className="text-sm text-red-600 mt-1">{paymentMethodErrors.name.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="payment-code">Codice</Label>
              <Input
                id="payment-code"
                {...registerPaymentMethod('code')}
                placeholder="Es: BANK_TRANSFER"
              />
            </div>
            <div>
              <Label htmlFor="payment-description">Descrizione</Label>
              <Input
                id="payment-description"
                {...registerPaymentMethod('description')}
                placeholder="Es: Pagamento tramite bonifico bancario"
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="payment-active"
                checked={paymentMethodIsActive}
                onChange={(e) => setPaymentMethodValue('is_active', e.target.checked)}
                className="w-4 h-4"
              />
              <Label htmlFor="payment-active" className="cursor-pointer">Attivo</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowPaymentMethodDialog(false)}>
                Annulla
              </Button>
              <Button type="submit">
                <Save className="w-4 h-4 mr-2" />
                {editingPaymentMethod ? 'Salva modifiche' : 'Crea'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Conferma Eliminazione Corriere */}
      <Dialog open={!!carrierToDelete} onOpenChange={() => setCarrierToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare il corriere "{carrierToDelete?.name}"? Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCarrierToDelete(null)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleDeleteCarrier}>
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Conferma Eliminazione Modalità Pagamento */}
      <Dialog open={!!paymentMethodToDelete} onOpenChange={() => setPaymentMethodToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare la modalità di pagamento "{paymentMethodToDelete?.name}"? Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPaymentMethodToDelete(null)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleDeletePaymentMethod}>
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog Tipo Collo */}
      <Dialog open={showPackageTypeDialog} onOpenChange={setShowPackageTypeDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingPackageType ? 'Modifica Tipo Collo' : 'Nuovo Tipo Collo'}</DialogTitle>
            <DialogDescription>
              {editingPackageType ? 'Modifica i dati del tipo di collo' : 'Inserisci i dati del nuovo tipo di collo'}
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={handleSubmitPackageType(handleSubmitPackageTypeForm)} className="space-y-4">
            <div>
              <Label htmlFor="package-type-name">Nome *</Label>
              <Input
                id="package-type-name"
                {...registerPackageType('name')}
                placeholder="Es: EPAL a rendere"
              />
              {packageTypeErrors.name && (
                <p className="text-sm text-red-600 mt-1">{packageTypeErrors.name.message}</p>
              )}
            </div>
            <div>
              <Label htmlFor="package-type-code">Codice</Label>
              <Input
                id="package-type-code"
                {...registerPackageType('code')}
                placeholder="Es: EPAL_RETURN"
              />
            </div>
            <div>
              <Label htmlFor="package-type-description">Descrizione</Label>
              <Input
                id="package-type-description"
                {...registerPackageType('description')}
                placeholder="Es: Pallet EPAL a rendere"
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="package-type-active"
                checked={packageTypeIsActive}
                onChange={(e) => setPackageTypeValue('is_active', e.target.checked)}
                className="w-4 h-4"
              />
              <Label htmlFor="package-type-active" className="cursor-pointer">Attivo</Label>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setShowPackageTypeDialog(false)}>
                Annulla
              </Button>
              <Button type="submit">
                <Save className="w-4 h-4 mr-2" />
                {editingPackageType ? 'Salva modifiche' : 'Crea'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog Conferma Eliminazione Tipo Collo */}
      <Dialog open={!!packageTypeToDelete} onOpenChange={() => setPackageTypeToDelete(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Conferma Eliminazione</DialogTitle>
            <DialogDescription>
              Sei sicuro di voler eliminare il tipo di collo "{packageTypeToDelete?.name}"? Questa azione non può essere annullata.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setPackageTypeToDelete(null)}>
              Annulla
            </Button>
            <Button variant="destructive" onClick={handleDeletePackageType}>
              Elimina
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};
